/**
 * Catalogue Page
 *
 * List of available category catalogues for download
 * Tasks: T5.3.3, T5.3.4 - Catalogue page and download prompt
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Box,
  Spinner,
  Banner,
  InlineGrid,
  Modal,
  TextField,
} from '@shopify/polaris';
import { ExportIcon } from '@shopify/polaris-icons';
import { listCatalogues, downloadCatalogue, type CatalogueItem } from '../services/inquiry.service';
import { useUIStore } from '../store/uiStore';

const CATALOGUE_CONTACT_KEY = 'catalogue_contact';

function getStoredContact(): { phone?: string; email?: string } | null {
  try {
    const stored = localStorage.getItem(CATALOGUE_CONTACT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function storeContact(contact: { phone?: string; email?: string }) {
  localStorage.setItem(CATALOGUE_CONTACT_KEY, JSON.stringify(contact));
}

export function CataloguePage() {
  const navigate = useNavigate();
  const { showToast } = useUIStore();

  const [catalogues, setCatalogues] = useState<CatalogueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedCatalogue, setSelectedCatalogue] = useState<CatalogueItem | null>(null);
  const [contactInput, setContactInput] = useState('');
  const [storedContact, setStoredContact] = useState(getStoredContact);

  useEffect(() => {
    const fetchCatalogues = async () => {
      try {
        const data = await listCatalogues();
        setCatalogues(data);
      } catch (error) {
        console.error('Failed to fetch catalogues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCatalogues();
  }, []);

  const triggerDownload = async (catalogue: CatalogueItem) => {
    setDownloadingId(catalogue.id);
    try {
      const result = await downloadCatalogue(catalogue.id);

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the blob URL after download
      setTimeout(() => {
        URL.revokeObjectURL(result.downloadUrl);
      }, 1000);

      showToast(`Catalogue "${result.fileName}" downloaded successfully!`, 'success');
    } catch (error) {
      console.error('Download error:', error);
      showToast('Failed to download catalogue. Please try again.', 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownload = async (catalogue: CatalogueItem) => {
    // If we already have stored contact info, download directly
    if (storedContact) {
      triggerDownload(catalogue);
      return;
    }

    // Otherwise, prompt for contact info
    setSelectedCatalogue(catalogue);
    setContactInput('');
    setContactModalOpen(true);
  };

  const handleContactSubmit = () => {
    const trimmed = contactInput.trim();
    if (!trimmed) return;

    // Detect if input is email or phone
    const isEmail = trimmed.includes('@');
    const contact = isEmail ? { email: trimmed } : { phone: trimmed };

    storeContact(contact);
    setStoredContact(contact);
    setContactModalOpen(false);

    // Proceed with download
    if (selectedCatalogue) {
      triggerDownload(selectedCatalogue);
    }
  };

  const handleChangeContact = () => {
    localStorage.removeItem(CATALOGUE_CONTACT_KEY);
    setStoredContact(null);
  };

  if (loading) {
    return (
      <Page title="Product Catalogues">
        <Box padding="800">
          <BlockStack align="center">
            <Spinner size="large" />
            <Text as="p">Loading catalogues...</Text>
          </BlockStack>
        </Box>
      </Page>
    );
  }

  return (
    <Page
      title="Product Catalogues"
      subtitle="Download our category-wise product catalogues"
    >
      <Layout>
        <Layout.Section>
          <Banner tone="info">
            Download our comprehensive catalogues to browse products offline.
            Each catalogue contains detailed product information, images, and pricing.
          </Banner>
          {storedContact && (
            <Box paddingBlockStart="200">
              <InlineStack gap="200" align="end">
                <Text as="p" variant="bodySm" tone="subdued">
                  Downloading as {storedContact.phone || storedContact.email}
                </Text>
                <Button variant="plain" size="slim" onClick={handleChangeContact}>
                  Change
                </Button>
              </InlineStack>
            </Box>
          )}
        </Layout.Section>

        <Layout.Section>
          {catalogues.length === 0 ? (
            <Card>
              <BlockStack gap="400" align="center">
                <Text as="h2" variant="headingMd">No catalogues available</Text>
                <Text as="p" tone="subdued">
                  Please check back later for our product catalogues.
                </Text>
              </BlockStack>
            </Card>
          ) : (
            <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="400">
              {catalogues.map((catalogue) => (
                <CatalogueCard
                  key={catalogue.id}
                  catalogue={catalogue}
                  onDownload={() => handleDownload(catalogue)}
                  downloading={downloadingId === catalogue.id}
                />
              ))}
            </InlineGrid>
          )}
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="300" align="center">
              <Text as="h2" variant="headingMd" alignment="center">
                Need a Custom Catalogue?
              </Text>
              <Text as="p" variant="bodyMd" alignment="center" tone="subdued">
                Contact us for customized catalogues based on your specific requirements.
              </Text>
              <Button onClick={() => navigate('/contact')}>
                Contact Us
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Contact Collection Modal */}
      <Modal
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        title="Download Catalogue"
        primaryAction={{
          content: 'Download',
          onAction: handleContactSubmit,
          disabled: !contactInput.trim(),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setContactModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              Enter your phone number or email so we can reach you about the{' '}
              <Text as="span" fontWeight="semibold">
                {selectedCatalogue?.name}
              </Text>{' '}
              catalogue.
            </Text>
            <TextField
              label="Phone number or email"
              value={contactInput}
              onChange={setContactInput}
              autoComplete="tel"
              placeholder="e.g. 9876543210 or name@company.com"
            />
            <Text as="p" variant="bodySm" tone="subdued">
              We&apos;ll only use this to follow up on your interest. No spam.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

interface CatalogueCardProps {
  catalogue: CatalogueItem;
  onDownload: () => void;
  downloading: boolean;
}

function CatalogueCard({ catalogue, onDownload, downloading }: CatalogueCardProps) {
  return (
    <Card>
      <BlockStack gap="300">
        {/* Banner Image */}
        {catalogue.bannerUrl ? (
          <div style={{ borderRadius: '8px', overflow: 'hidden', marginTop: '-16px', marginLeft: '-16px', marginRight: '-16px' }}>
            <img
              src={catalogue.bannerUrl}
              alt={catalogue.name}
              style={{
                width: '100%',
                height: '120px',
                objectFit: 'cover',
              }}
            />
          </div>
        ) : (
          <Box
            minHeight="120px"
            background="bg-surface-secondary"
            borderRadius="200"
          />
        )}

        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            {catalogue.name}
          </Text>

          {catalogue.description && (
            <Text as="p" variant="bodySm" tone="subdued">
              {catalogue.description.length > 100
                ? `${catalogue.description.substring(0, 100)}...`
                : catalogue.description}
            </Text>
          )}

          <InlineStack align="space-between" blockAlign="center">
            <Text as="p" variant="bodySm">
              {catalogue.productCount} products
            </Text>
            {catalogue.catalogueGeneratedAt && (
              <Text as="p" variant="bodySm" tone="subdued">
                Updated: {new Date(catalogue.catalogueGeneratedAt).toLocaleDateString('en-IN')}
              </Text>
            )}
          </InlineStack>
        </BlockStack>

        <Button
          fullWidth
          icon={ExportIcon}
          onClick={onDownload}
          loading={downloading}
        >
          Download Catalogue
        </Button>
      </BlockStack>
    </Card>
  );
}
